/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useSuspenseQuery } from '@apollo/client';
import { isNotNil } from 'es-toolkit';
import { useParams } from 'react-router-dom';

import { initialValues } from '@fai/friendli-suite/features/products/dedicated-endpoints/endpoints/form-context/initial-values';
import { DedicatedModelType } from '@fai/friendli-suite/features/types/dedicated-endpoints/models';
import { DedicatedFinetuningHeaderDocument } from '@fai/friendli-suite/generated/client/graphql';
import { getUniqueName } from '@fai/util';

export const useFinetuningDetailHeader = () => {
  const { finetuningId = '' } = useParams();

  const { data: _data } = useSuspenseQuery(DedicatedFinetuningHeaderDocument, {
    variables: { finetuningId },
    fetchPolicy: 'cache-and-network',
  });

  const adapters = _data.dedicatedFinetuning?.adapters
    ? _data.dedicatedFinetuning.adapters.edges.map(({ node }) => ({
        id: node.id,
        name: node.name,
        route: getUniqueName(),
        editDisabled: true,
      }))
    : [];

  const model = {
    ...initialValues.model,
    ...(isNotNil(_data.dedicatedFinetuning?.model?.id)
      ? {
          type: DedicatedModelType.Native,
          native: {
            ...initialValues.model.native,
            modelId: _data.dedicatedFinetuning.model.id ?? initialValues.model.native.modelId,
            modelName: _data.dedicatedFinetuning.model.name ?? initialValues.model.native.modelName,
            adapters,
          },
        }
      : isNotNil(_data.dedicatedFinetuning?.wandbModel)
      ? {
          type: DedicatedModelType.Wandb,
          wandb: {
            ...initialValues.model.wandb,
            artifactName:
              _data.dedicatedFinetuning.wandbModel ?? initialValues.model.wandb.artifactName,
            adapters,
          },
        }
      : {
          type: DedicatedModelType.HuggingFace,
          huggingFace: {
            ...initialValues.model.huggingFace,
            repositoryName:
              _data.dedicatedFinetuning?.hfModelRepo ??
              initialValues.model.huggingFace.repositoryName,
            repositoryRevision:
              _data.dedicatedFinetuning?.hfModelRepoRevision ??
              initialValues.model.huggingFace.repositoryRevision,
            adapters,
          },
        }),
  };

  const data = {
    id: _data.dedicatedFinetuning?.id,
    name: _data.dedicatedFinetuning?.name,
    model,
  };

  return { data };
};
